import React from 'react';
import type { ComponentProps, ReactNode } from 'react';
import styled from '@emotion/styled';
import type {
  BorderRadiusProps,
  DisplayProps,
  FlexProps,
  HeightProps,
  OverflowProps,
  PositionProps,
  TextAlignProps,
  WidthProps,
} from 'styled-system';
import {
  borderRadius,
  display,
  height,
  overflow,
  position,
  space,
  textAlign,
  width,
} from 'styled-system';

import type { SpaceProps } from 'common/theme/system-types';
import Video from '../../../product-gallery/components/MainCarousel/Video';
import type { ThemeTopProps } from '../../theme/system-utilities';
import { themeTop } from '../../theme/system-utilities';
import ResponsiveImage from '../ResponsiveImage';
import { type CarouselSlideProps } from './types';

interface CarouselSlideContentProps extends CarouselSlideProps {
  renderSlide?: (props: CarouselSlideProps) => ReactNode;
  isLazy?: boolean;
  highPriority?: boolean;
}

export const StyledResponsiveImage = styled(ResponsiveImage)<
  WidthProps & DisplayProps & PositionProps & ThemeTopProps & BorderRadiusProps
>`
  ${borderRadius};
  ${display};
  ${height};
  ${position};
  ${themeTop};
  ${width};
`;

export const styledResponsiveImageStyles: Partial<ComponentProps<typeof StyledResponsiveImage>> = {
  borderRadius: 'small',
  display: 'block',
  height: '100%',
  position: 'absolute',
  themeTop: 'sp_0',
  width: 1,
};

export const ImageWrapper = styled.div<
  DisplayProps &
    TextAlignProps &
    PositionProps &
    SpaceProps &
    WidthProps &
    FlexProps &
    OverflowProps &
    HeightProps
>`
  ${display};
  ${position};
  ${space};
  ${textAlign};
  ${width};
  ${overflow};
  ${height};
`;

export const imageWrapperStyles: ComponentProps<typeof ImageWrapper> = {
  display: 'block',
  position: 'relative',
  pt: '100%',
  textAlign: 'center',
  width: 1,
};

export const videoWrapperStyles: ComponentProps<typeof ImageWrapper> = {
  display: 'flex',
  position: 'relative',
  textAlign: 'center',
  flex: 1,
  overflow: 'hidden',
  height: '100%',
};

const CarouselSlideContent = ({
  cloneImage,
  currentIndex,
  itemIndex,
  setVideoPlayer,
  widths,
  renderSlide,
  isLazy,
  highPriority,
  name,
}: CarouselSlideContentProps) => {
  if (renderSlide) {
    return renderSlide({ cloneImage, currentIndex, itemIndex, setVideoPlayer, widths, name });
  }

  const isVideoContent = cloneImage.ytId && setVideoPlayer;

  return isVideoContent ? (
    <ImageWrapper {...videoWrapperStyles} data-testid="image-carousel-wrapper">
      <Video
        src={cloneImage.ytId || ''}
        alt={cloneImage.alt || name}
        aria-hidden="true"
        setVideoPlayer={setVideoPlayer}
        isActive={currentIndex === itemIndex}
        widths={widths}
      />
    </ImageWrapper>
  ) : (
    <ImageWrapper {...imageWrapperStyles} data-testid="image-carousel-wrapper">
      <StyledResponsiveImage
        {...styledResponsiveImageStyles}
        src={cloneImage.path}
        alt={cloneImage.alt || name}
        aria-hidden="true"
        widths={widths}
        isLazy={isLazy ?? itemIndex >= 1}
        highPriority={highPriority ?? itemIndex === 0}
        draggable={false}
      />
    </ImageWrapper>
  );
};

export default CarouselSlideContent;
